/*
 * CAPTURE-OWNED MODULE
 * Template Identifiers Component for capture-ui
 * Displays and manages text-based identifiers for template matching.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getIdentifiers, deleteIdentifier } from '../api/captureTemplates';
import IdentifierModal from './IdentifierModal';
import './TemplateIdentifiers.css';

const TYPE_META = {
  vendor_name:  { icon: '\u{1F3E2}', label: 'Vendor Name' },
  header_text:  { icon: '\u{1F4C4}', label: 'Header Text' },
  footer_text:  { icon: '\u{1F4CB}', label: 'Footer Text' },
  logo_text:    { icon: '\u{1F524}', label: 'Logo Text' },
  custom:       { icon: '\u{270F}\u{FE0F}', label: 'Custom Pattern' },
};

function getWeightLabel(weight) {
  if (weight <= 0.5) return 'Minor';
  if (weight <= 1.0) return 'Normal';
  if (weight <= 2.0) return 'Critical';
  return 'Essential';
}

const TemplateIdentifiers = ({ templateId, onRequestPickWord, pendingPrefillValue, onPrefillConsumed }) => {
  const [identifiers, setIdentifiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingIdentifier, setEditingIdentifier] = useState(null);
  const [prefillValue, setPrefillValue] = useState('');

  // When a word is picked from the document, open the modal pre-populated
  useEffect(() => {
    if (pendingPrefillValue) {
      setPrefillValue(pendingPrefillValue);
      setEditingIdentifier(null);
      setShowModal(true);
      if (onPrefillConsumed) onPrefillConsumed();
    }
  }, [pendingPrefillValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadIdentifiers = useCallback(async () => {
    if (!templateId) return;
    try {
      setLoading(true);
      const resp = await getIdentifiers(templateId);
      setIdentifiers(resp.identifiers || []);
      setError(null);
    } catch (err) {
      console.error('[identifiers] Load failed:', err);
      setError('Failed to load identifiers');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadIdentifiers();
  }, [loadIdentifiers]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this identifier? This cannot be undone.')) return;
    try {
      await deleteIdentifier(templateId, id);
      setIdentifiers(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('[identifiers] Delete failed:', err);
      alert('Failed to delete identifier');
    }
  };

  const handleEdit = (identifier) => {
    setEditingIdentifier(identifier);
    setShowModal(true);
  };

  const handleModalClose = (saved) => {
    setShowModal(false);
    setEditingIdentifier(null);
    setPrefillValue('');
    if (saved) loadIdentifiers();
  };

  if (loading) {
    return <div className="ti-loading">Loading identifiers...</div>;
  }

  return (
    <div className="template-identifiers">
      <div className="ti-header">
        <div>
          <h3>Template Identifiers</h3>
          <p className="ti-subtitle">
            Text patterns that uniquely identify this template when OCR is unavailable.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              if (onRequestPickWord) {
                onRequestPickWord();
              } else {
                setPrefillValue('');
                setShowModal(true);
              }
            }}
            title={onRequestPickWord
              ? 'Click a word on the document to use as identifier text'
              : 'Add an identifier by typing text manually'}
          >
            + Add Identifier
          </button>
          {onRequestPickWord && (
            <span style={{ fontSize: '10px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
              Click a word on the document
            </span>
          )}
        </div>
      </div>

      {error && <div className="ti-error">{error}</div>}

      {identifiers.length === 0 ? (
        <div className="ti-empty">
          <p>No identifiers defined yet.</p>
          <p className="ti-empty-hint">
            {onRequestPickWord
              ? 'Click "+ Add Identifier" then click any text on the document — vendor name, header text, or a unique footer pattern — to capture it as an identifier.'
              : 'Add identifiers like vendor name, header text, or unique footer patterns to help match this template when OCR is unavailable.'}
          </p>
        </div>
      ) : (
        <div className="ti-list">
          {identifiers.map(item => {
            const meta = TYPE_META[item.identifier_type] || TYPE_META.custom;
            return (
              <div key={item.id} className="ti-card">
                <div className="ti-card-top">
                  <div className="ti-type">
                    <span className="ti-icon">{meta.icon}</span>
                    <span className="ti-type-label">
                      {meta.label}
                      {item.is_required && <span className="ti-required">Required</span>}
                    </span>
                  </div>
                  <div className="ti-actions">
                    <button className="btn-icon" onClick={() => handleEdit(item)} title="Edit">
                      ✏️
                    </button>
                    <button className="btn-icon" onClick={() => handleDelete(item.id)} title="Delete">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="ti-value">"{item.identifier_value}"</div>

                <div className="ti-meta">
                  <span className="ti-match-mode">Match: {item.match_mode}</span>
                  <span className={`ti-weight ti-weight-${getWeightLabel(Number(item.weight)).toLowerCase()}`}>
                    {getWeightLabel(Number(item.weight))} ({Number(item.weight).toFixed(1)})
                  </span>
                </div>

                {item.description && (
                  <div className="ti-description">{item.description}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <IdentifierModal
          templateId={templateId}
          identifier={editingIdentifier}
          onClose={handleModalClose}
          prefillValue={prefillValue || undefined}
        />
      )}
    </div>
  );
};

export default TemplateIdentifiers;
